/**
 * Map Layers Zustand Slice
 *
 * Manages custom map layers: CRUD operations, visibility toggling,
 * and GeoJSON data caching for map rendering.
 *
 * Also manages standard layer visibility with server-side persistence
 * via layerPreferencesService (debounced save on every toggle).
 *
 * Pattern: notesSlice (immer mutations, apiClient calls, mapper transforms)
 */

import { layersService } from '@/services/api';
import {
  layerPreferencesService,
  type LayerPreferencesData,
} from '@/services/api/layerPreferencesService';
import {
  DEFAULT_VISIBILITY,
  DEFAULT_ARTIFACT_VISIBILITY,
} from '@/types/layers';
import type { LayerVisibility, ArtifactVisibility } from '@/types/layers';
import type { UploadLayerData } from '@/services/api/layersService';
import type { UpdateLayerRequest } from '@/types/api/requests/layers';
import type { ApiError } from '@/types/api/errors';
import type { MapLayer } from '@/types/layers';
import { scopedOutLayerKeys } from '@/config/mapFeatures';
import { mapLayerFromApi } from '../mappers/layerMapper';
import {
  selectActiveWorkspaceCountryCode,
  selectIsReadOnlyViewer,
} from '../selectors';
import type { GeoMarkupStore } from '../index';

/** Debounce window for persisting layer visibility preferences (exported for tests). */
export const SAVE_DEBOUNCE_MS = 1000;

// Module-level debounce timer for save preferences (supports cancellation on workspace switch)
let savePreferencesTimeout: ReturnType<typeof setTimeout> | null = null;

export interface MapLayersSlice {
  mapLayers: {
    items: MapLayer[];
    isLoading: boolean;
    hasLoaded: boolean;
    isSyncing: boolean;
    error: ApiError | null;

    /** Custom layer visibility overrides (layerId -> visible) */
    customLayerVisibility: Record<string, boolean>;

    /** Standard layer visibility (polandBorder, nationalRoads, etc.) */
    standardLayerVisibility: LayerVisibility;

    /** Artifact layer visibility (tasks, notes on map) */
    artifactLayerVisibility: ArtifactVisibility;

    // Actions
    fetchLayers: () => Promise<void>;
    uploadLayer: (data: UploadLayerData) => Promise<MapLayer | null>;
    updateLayer: (id: string, data: UpdateLayerRequest) => Promise<boolean>;
    deleteLayer: (id: string) => Promise<boolean>;
    toggleCustomLayerVisibility: (layerId: string) => void;
    setCustomLayerVisible: (layerId: string, visible: boolean) => void;
    toggleStandardLayer: (key: keyof LayerVisibility) => void;
    toggleArtifactLayer: (key: keyof ArtifactVisibility) => void;
    saveLayerPreferences: () => Promise<void>;
    clearLayers: () => void;
  };
}

function scheduleSave(getState: () => MapLayersSlice) {
  // Read-only viewers have no authenticated session — layer toggles are
  // per-visit only, never persisted to the preferences API.
  if (selectIsReadOnlyViewer(getState() as GeoMarkupStore)) return;
  if (savePreferencesTimeout) clearTimeout(savePreferencesTimeout);
  savePreferencesTimeout = setTimeout(() => {
    savePreferencesTimeout = null;
    getState().mapLayers.saveLayerPreferences();
  }, SAVE_DEBOUNCE_MS);
}

function cancelPendingSave() {
  if (savePreferencesTimeout) {
    clearTimeout(savePreferencesTimeout);
    savePreferencesTimeout = null;
  }
}

export const createMapLayersSlice = (
  set: (fn: (state: MapLayersSlice) => void) => void,
  get: () => MapLayersSlice,
  _store: unknown
) => ({
  mapLayers: {
    items: [] as MapLayer[],
    isLoading: false,
    hasLoaded: false,
    isSyncing: false,
    error: null as ApiError | null,
    customLayerVisibility: {} as Record<string, boolean>,
    standardLayerVisibility: { ...DEFAULT_VISIBILITY },
    artifactLayerVisibility: { ...DEFAULT_ARTIFACT_VISIBILITY },

    fetchLayers: async () => {
      set((state) => {
        state.mapLayers.isLoading = true;
        state.mapLayers.error = null;
      });

      try {
        // Fetch layers and preferences in parallel
        const [response, prefResponse] = await Promise.all([
          layersService.getAll(),
          layerPreferencesService.get().catch(() => null),
        ]);

        const layers = response.data.layers.map(mapLayerFromApi);

        // Parse preferences once (before state update)
        let prefs: LayerPreferencesData | null = null;
        if (prefResponse?.data.preferences) {
          try {
            prefs = JSON.parse(prefResponse.data.preferences);
          } catch {
            // Invalid JSON — ignore
          }
        }

        // Country scoping: read the workspace country once so the force-off
        // below (scoped-out layers) survives saved prefs.
        const countryCode = selectActiveWorkspaceCountryCode(
          get() as GeoMarkupStore
        );

        set((state) => {
          state.mapLayers.items = layers;
          state.mapLayers.isLoading = false;
          state.mapLayers.hasLoaded = true;

          // Initialize visibility for new layers (keep existing overrides)
          for (const layer of layers) {
            if (!(layer.id in state.mapLayers.customLayerVisibility)) {
              state.mapLayers.customLayerVisibility[layer.id] = layer.isVisible;
            }
          }

          // Apply saved preferences if available
          if (prefs) {
            if (prefs.standardLayers) {
              state.mapLayers.standardLayerVisibility = {
                ...DEFAULT_VISIBILITY,
                ...prefs.standardLayers,
              };
            }

            if (prefs.customLayers) {
              for (const layer of layers) {
                if (layer.id in prefs.customLayers) {
                  state.mapLayers.customLayerVisibility[layer.id] =
                    prefs.customLayers[layer.id] ?? layer.isVisible;
                }
              }
            }

            if (prefs.artifactLayers) {
              state.mapLayers.artifactLayerVisibility = {
                ...DEFAULT_ARTIFACT_VISIBILITY,
                ...prefs.artifactLayers,
              };
            }
          }

          // Force standard layers that are out of scope for the workspace's
          // country off, overriding both defaults and any saved preferences
          // above, so saved prefs can never re-enable a layer with no data
          // for that country.
          for (const layerKey of scopedOutLayerKeys(countryCode)) {
            state.mapLayers.standardLayerVisibility[layerKey] = false;
          }
        });
      } catch (error) {
        set((state) => {
          state.mapLayers.isLoading = false;
          state.mapLayers.hasLoaded = true;
          state.mapLayers.error = error as ApiError;
        });
      }
    },

    uploadLayer: async (data: UploadLayerData): Promise<MapLayer | null> => {
      set((state) => {
        state.mapLayers.isSyncing = true;
        state.mapLayers.error = null;
      });

      try {
        const response = await layersService.upload(data);
        const layer = mapLayerFromApi(response.data.layer);

        set((state) => {
          state.mapLayers.items.push(layer);
          state.mapLayers.customLayerVisibility[layer.id] = layer.isVisible;
          state.mapLayers.isSyncing = false;
        });

        return layer;
      } catch (error) {
        set((state) => {
          state.mapLayers.isSyncing = false;
          state.mapLayers.error = error as ApiError;
        });
        return null;
      }
    },

    updateLayer: async (
      id: string,
      data: UpdateLayerRequest
    ): Promise<boolean> => {
      set((state) => {
        state.mapLayers.isSyncing = true;
        state.mapLayers.error = null;
      });

      try {
        const response = await layersService.update(id, data);
        const updated = mapLayerFromApi(response.data.layer);

        set((state) => {
          const index = state.mapLayers.items.findIndex(
            (layer) => layer.id === id
          );
          if (index !== -1) {
            state.mapLayers.items[index] = updated;
          }
          state.mapLayers.isSyncing = false;
        });

        return true;
      } catch (error) {
        set((state) => {
          state.mapLayers.isSyncing = false;
          state.mapLayers.error = error as ApiError;
        });
        return false;
      }
    },

    deleteLayer: async (id: string): Promise<boolean> => {
      set((state) => {
        state.mapLayers.isSyncing = true;
        state.mapLayers.error = null;
      });

      try {
        await layersService.delete(id);

        set((state) => {
          state.mapLayers.items = state.mapLayers.items.filter(
            (layer) => layer.id !== id
          );
          delete state.mapLayers.customLayerVisibility[id];
          state.mapLayers.isSyncing = false;
        });

        scheduleSave(get);

        return true;
      } catch (error) {
        set((state) => {
          state.mapLayers.isSyncing = false;
          state.mapLayers.error = error as ApiError;
        });
        return false;
      }
    },

    toggleCustomLayerVisibility: (layerId: string) => {
      set((state) => {
        const current = state.mapLayers.customLayerVisibility[layerId] ?? true;
        state.mapLayers.customLayerVisibility[layerId] = !current;
      });
      scheduleSave(get);
    },

    setCustomLayerVisible: (layerId: string, visible: boolean) => {
      set((state) => {
        state.mapLayers.customLayerVisibility[layerId] = visible;
      });
      scheduleSave(get);
    },

    toggleStandardLayer: (key: keyof LayerVisibility) => {
      set((state) => {
        state.mapLayers.standardLayerVisibility[key] =
          !state.mapLayers.standardLayerVisibility[key];
      });
      scheduleSave(get);
    },

    toggleArtifactLayer: (key: keyof ArtifactVisibility) => {
      set((state) => {
        state.mapLayers.artifactLayerVisibility[key] =
          !state.mapLayers.artifactLayerVisibility[key];
      });
      scheduleSave(get);
    },

    saveLayerPreferences: async () => {
      const state = get();
      const {
        standardLayerVisibility,
        customLayerVisibility,
        artifactLayerVisibility,
      } = state.mapLayers;

      const preferences: LayerPreferencesData = {
        standardLayers: { ...standardLayerVisibility },
        customLayers: { ...customLayerVisibility },
        artifactLayers: { ...artifactLayerVisibility },
      };

      try {
        await layerPreferencesService.update(preferences);
      } catch {
        // Silently fail - preferences are non-critical
      }
    },

    clearLayers: () => {
      // Cancel any pending debounced save to prevent stale workspace writes
      cancelPendingSave();

      set((state) => {
        state.mapLayers.items = [];
        state.mapLayers.hasLoaded = false;
        state.mapLayers.customLayerVisibility = {};
        state.mapLayers.standardLayerVisibility = { ...DEFAULT_VISIBILITY };
        state.mapLayers.artifactLayerVisibility = {
          ...DEFAULT_ARTIFACT_VISIBILITY,
        };
        state.mapLayers.error = null;
      });
    },
  },
});
